import { useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { config } from '../lib/config';
import { useLiveAircraft, type LiveAircraft } from '../hooks/useLiveAircraft';
import { Icon } from '../components/Icon';

const esc = (value: string) =>
  value.replace(
    /[&<>'"]/g,
    (x) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[x]!,
  );

const fmtAlt = (m: number | null) => (m == null ? '—' : `${Math.round(m / 0.3048).toLocaleString('de-DE')} ft`);
const fmtSpd = (ms: number | null) => (ms == null ? '—' : `${Math.round(ms * 1.944)} kn`);
const fmtTime = (epoch: number) =>
  new Date(epoch * 1000).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

const MIL_HINTS = ['RCH', 'GAF', 'NAF', 'BAF', 'DAF', 'HKY', 'JAKE', 'DUKE', 'MCF', 'SPAR', 'IBIS', 'SNAKE', 'VADER', 'COPTER', 'NATO'];

const isSuspicious = (a: LiveAircraft) => {
  const cs = (a.callsign ?? '').toUpperCase();
  return MIL_HINTS.some((h) => cs.startsWith(h)) || (a.velocity !== null && a.velocity > 260 && (a.altitude ?? 0) > 9000);
};

export function LivePage() {
  const { aircraft, status, error, refresh } = useLiveAircraft();
  const host = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const [fallback, setFallback] = useState('');
  const [query, setQuery] = useState('');
  const [milOnly, setMilOnly] = useState(false);
  const [selected, setSelected] = useState<LiveAircraft | null>(null);
  const [tick, setTick] = useState<{ time: string; count: number } | null>(null);

  const visible = useMemo(
    () =>
      aircraft.filter(
        (a) =>
          (!milOnly || isSuspicious(a)) &&
          (!query.trim() || (a.callsign ?? '').toUpperCase().includes(query.trim().toUpperCase()) || a.icao24.toUpperCase().includes(query.trim().toUpperCase())),
      ),
    [aircraft, milOnly, query],
  );

  // Map init
  useEffect(() => {
    const container = host.current;
    if (!container) return;
    if (!config.mapboxToken) {
      setFallback('Map service is not configured. Add VITE_MAPBOX_TOKEN to continue.');
      return;
    }
    if (!mapboxgl.supported()) {
      setFallback('WebGL is unavailable on this device.');
      return;
    }
    mapboxgl.accessToken = config.mapboxToken;
    const map = new mapboxgl.Map({
      container,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: [17.5, 56.5],
      zoom: 4.6,
      attributionControl: false,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right');
    mapRef.current = map;
    return () => {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Markers
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !config.mapboxToken) return;
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];
    for (const a of visible) {
      const el = document.createElement('div');
      el.className = `ac-marker${isSuspicious(a) ? ' ac-marker--mil' : ''}`;
      const deg = a.heading ?? 0;
      el.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2 5 22l7-5 7 5z"/></svg>`;
      el.style.transform = `rotate(${deg}deg)`;
      const popup = new mapboxgl.Popup({ offset: 10, closeButton: false, maxWidth: '280px' });
      const cs = esc(a.callsign ?? a.icao24.toUpperCase());
      popup.setHTML(
        `<div class="ac-pop"><span class="event-type event-type--blue">${isSuspicious(a) ? 'SUSPECT' : 'AIR'}</span><h3>${cs}</h3><dl>
        <div><dt>Alt</dt><dd>${fmtAlt(a.altitude)}</dd></div>
        <div><dt>Spd</dt><dd>${fmtSpd(a.velocity)}</dd></div>
        <div><dt>Track</dt><dd>${Math.round(a.heading ?? 0)}°</dd></div>
        <div><dt>Origin</dt><dd>${esc(a.originCountry)}</dd></div>
        <div><dt>Last</dt><dd>${fmtTime(a.lastSeen)}</dd></div>
        </dl></div>`,
      );
      const marker = new mapboxgl.Marker({ element: el }).setLngLat([a.lng, a.lat]).setPopup(popup).addTo(map);
      markersRef.current.push(marker);
    }
    setTick({ time: new Date().toLocaleTimeString('de-DE'), count: visible.length });
  }, [visible]);

  const live = status === 'live';

  return (
    <div className="page" data-testid="live-page">
      <section className="page-intro">
        <div>
          <span className="eyebrow">Live domain awareness</span>
          <h1>Live air picture</h1>
          <p>ADS-B transponders over the Baltic &amp; Northern Europe — refreshed periodically (OpenSky free tier).</p>
        </div>
        <div className="live-status">
          <span className={`live-dot${live ? ' live-dot--on' : ''}`} />
          {live ? 'LIVE' : status === 'loading' ? 'SYNC' : status === 'error' ? 'OFFLINE' : 'IDLE'}
        </div>
      </section>

      <section className="toolbar">
        <label className="search">
          <Icon name="search" size={16} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Callsign / ICAO24 …" />
        </label>
        <button className={`chip${milOnly ? ' chip--on' : ''}`} onClick={() => setMilOnly((v) => !v)}>
          SUSPECT-Filter
        </button>
        <button className="chip" onClick={() => void refresh()} title="Refresh now">
          ⟳
        </button>
      </section>

      <div className="live-grid">
        <div className="map-host">
          {fallback ? (
            <div className="empty-state">{fallback}</div>
          ) : (
            <div ref={host} className="map-canvas" data-testid="live-map" />
          )}
          <div className="map-hud map-hud--tl">
            <span className="mono">SCOPE · BALTIC</span>
          </div>
          <div className="map-hud map-hud--bl">
            <span className="mono">{visible.length} TRACKS{query.trim() || milOnly ? ' (filtered)' : ''}</span>
            {tick && <span className="mono dim">@{tick.time}</span>}
          </div>
        </div>

        <aside className="live-rail">
          <div className="panel">
            <div className="panel__head">
              <span className="eyebrow">SIGNAL LOG</span>
              <span className="mono dim">{aircraft.length} total</span>
            </div>
            <div className="signal-log">
              {visible.slice(0, 14).map((a) => (
                <button key={a.icao24} className="signal-row" onClick={() => setSelected(a)}>
                  <span className={`signal-row__dot${isSuspicious(a) ? ' signal-row__dot--warn' : ''}`} />
                  <span className="mono signal-row__cs">{a.callsign ?? a.icao24.toUpperCase()}</span>
                  <span className="mono dim">{fmtAlt(a.altitude)}</span>
                  <span className="mono dim">{fmtSpd(a.velocity)}</span>
                </button>
              ))}
              {visible.length === 0 && (
                <div className="empty-state">
                  {status === 'error'
                    ? `Feed offline — Live-Proxy wird eingerichtet (${error})`
                    : 'No tracks in view.'}
                </div>
              )}
            </div>
          </div>

          {selected && (
            <div className="panel">
              <div className="panel__head">
                <span className="eyebrow">TRACK DETAIL</span>
                <button className="chip" onClick={() => setSelected(null)}>
                  ×
                </button>
              </div>
              <h3 className="mono">{selected.callsign ?? selected.icao24.toUpperCase()}</h3>
              <dl className="kv">
                <div><dt>ICAO24</dt><dd className="mono">{selected.icao24.toUpperCase()}</dd></div>
                <div><dt>Altitude</dt><dd>{fmtAlt(selected.altitude)}</dd></div>
                <div><dt>Speed</dt><dd>{fmtSpd(selected.velocity)}</dd></div>
                <div><dt>Heading</dt><dd>{Math.round(selected.heading ?? 0)}°</dd></div>
                <div><dt>Position</dt><dd className="mono">{selected.lat.toFixed(3)}, {selected.lng.toFixed(3)}</dd></div>
                <div><dt>Origin</dt><dd>{selected.originCountry}</dd></div>
                <div><dt>Last seen</dt><dd>{fmtTime(selected.lastSeen)}</dd></div>
              </dl>
            </div>
          )}
        </aside>
      </div>

      <p className="fineprint">
        Source: OpenSky Network (public ADS-B) via CORS proxy. Data is public; display is delayed by network latency and
        polling interval. Suspect heuristic is a rough filter — verify before acting.
      </p>
    </div>
  );
}
